export interface ModelInfo {
  value: string;
  label: string;
  icon: string;
  contextLength?: string;
  credits: string | number;
  isVision?: boolean;
  isThinking?: boolean;
}

export interface ModelCategory {
  category: string;
  models: ModelInfo[];
}

export const modelCategories: ModelCategory[] = [
  {
    category: 'Power AI',
    models: [
      {
        value: 'auto',
        label: 'Power AI',
        icon: 'logo-only200-removebg',
        contextLength: '200K',
        credits: 5,
        isVision: true
      }
    ]
  },
  {
    category: 'Premium Models',
    models: [
      {
        value: 'claude-opus-4-7',
        label: 'Claude Opus 4.7',
        icon: 'icon-claude',
        contextLength: '200K',
        credits: 150,
        isVision: true
      },
      {
        value: 'claude-4-7-opus-thinking',
        label: 'Claude Opus 4.7',
        icon: 'icon-claude',
        contextLength: '200K',
        credits: 300,
        isVision: true,
        isThinking: true
      },
      {
        value: 'claude-sonnet-4-6',
        label: 'Claude Sonnet 4.6',
        icon: 'icon-claude',
        contextLength: '1M',
        credits: 60,
        isVision: true
      },
      {
        value: 'claude-4-6-sonnet-thinking',
        label: 'Claude Sonnet 4.6',
        icon: 'icon-claude',
        contextLength: '1M',
        credits: 100,
        isVision: true,
        isThinking: true
      },
      {
        value: 'gpt-5.5',
        label: 'GPT-5.5',
        icon: 'icon-chatgpt',
        contextLength: '1M',
        credits: 80,
        isVision: true
      },
      {
        value: 'google/gemini-3.1-pro-preview',
        label: 'Gemini 3.1 Pro',
        icon: 'icon-gemini',
        contextLength: '1M',
        credits: 60,
        isVision: true
      },
      {
        value: 'grok-4.20-0309-reasoning',
        label: 'Grok 4.20',
        icon: 'icon-xai',
        contextLength: '2M',
        credits: 60,
        isVision: true,
        isThinking: true
      },
      {
        value: 'deepseek/deepseek-v4-pro',
        label: 'DeepSeek',
        icon: 'icon-deepseek',
        contextLength: '1M',
        credits: 30,
        isVision: false,
        isThinking: true
      },
      {
        value: 'sonar-reasoning-pro',
        label: 'Perplexity Pro',
        icon: 'icon-perplexity',
        contextLength: '128K',
        credits: 30,
        isVision: false,
        isThinking: true
      },
      {
        value: 'z-ai/glm-5.1',
        label: 'GLM 5.1',
        icon: 'icon-zai',
        contextLength: '200K',
        credits: 20,
        isVision: true,
        isThinking: true
      },
      {
        value: 'gpt-image-2',
        label: 'GPT Image 2',
        icon: 'icon-chatgpt',
        contextLength: '128K',
        credits: 100,
        isVision: true
      },
      {
        value: 'google/gemini-3.1-flash-image-preview',
        label: 'Nano Banana 2',
        icon: 'icon-gemini',
        contextLength: '64K',
        credits: 50,
        isVision: true
      },
      {
        value: 'black-forest-labs/FLUX.2-pro',
        label: 'FLUX 2 Pro',
        icon: 'icon-flux',
        contextLength: 'n/a',
        credits: 60,
        isVision: false
      }
    ]
  },
  {
    category: 'Basic Models',
    models: [
      {
        value: 'gpt-5.4-mini',
        label: 'GPT-5.4 Mini',
        icon: 'icon-chatgpt',
        contextLength: '400K',
        credits: 'unlimited',
        isVision: true
      },
      {
        value: 'claude-haiku-4-5-20251001',
        label: 'Claude Haiku 4.5',
        icon: 'icon-claude',
        contextLength: '200K',
        credits: 'unlimited',
        isVision: true
      },
      {
        value: 'google/gemini-3-flash-preview',
        label: 'Gemini 3 Flash',
        icon: 'icon-gemini',
        contextLength: '1M',
        credits: 'unlimited',
        isVision: true
      },
      {
        value: 'grok-4-fast-reasoning',
        label: 'Grok 4 Fast',
        icon: 'icon-xai',
        contextLength: '2M',
        credits: 'unlimited',
        isVision: true,
        isThinking: true
      },
      {
        value: 'deepseek/deepseek-v4-flash',
        label: 'DeepSeek',
        icon: 'icon-deepseek',
        contextLength: '1M',
        credits: 'unlimited',
        isVision: false
      },
      {
        value: 'qwen/qwen3.5-plus-02-15',
        label: 'Qwen 3.5 Plus',
        icon: 'icon-qwen',
        contextLength: '1M',
        credits: 'unlimited',
        isVision: false
      },
      {
        value: 'meta-llama/llama-4-maverick',
        label: 'Llama 4 Maverick',
        icon: 'icon-llama',
        contextLength: '1M',
        credits: 'unlimited',
        isVision: false
      },
      {
        value: 'moonshotai/kimi-k2.6',
        label: 'Kimi K2.6',
        icon: 'kimi',
        contextLength: '256K',
        credits: 'unlimited',
        isVision: false
      },
      {
        value: 'mistralai/mistral-medium-3.1',
        label: 'Mistral Medium 3.1',
        icon: 'icon-mistral',
        contextLength: '128K',
        credits: 'unlimited',
        isVision: false
      },
      {
        value: 'openai/gpt-oss-120b',
        label: 'GPT OSS 120B',
        icon: 'icon-chatgpt',
        contextLength: '128K',
        credits: 'unlimited',
        isVision: false
      }
    ]
  }
];

// Helper functions
export const getModelInfo = (modelValue: string): ModelInfo | undefined => {
  for (const category of modelCategories) {
    const model = category.models.find(m => m.value === modelValue);
    if (model) return model;
  }
  return undefined;
};

export const getModelIcon = (modelValue: string, theme: 'light' | 'dark'): string => {
  const model = getModelInfo(modelValue);
  if (!model) return '/icon-chatgpt.png';

  if (model.icon === 'logo-only200-removebg') {
    return theme === 'dark' ? '/logo-only200-removebg-inverted.png' : '/logo-only200-removebg.png';
  }
  return `/${model.icon}.png`;
};

export const isModelFree = (modelValue: string): boolean => {
  const model = getModelInfo(modelValue);
  return model?.credits === 'unlimited';
};

export const getModelCredit = (modelValue: string): string | number => {
  const model = getModelInfo(modelValue);
  return model?.credits || 0;
};

export const isModelLite = (modelValue: string): boolean => {
  const model = getModelInfo(modelValue);
  return model?.credits === 'unlimited';
};

export const isModelPremium = (modelValue: string): boolean => {
  const premiumModels = [
    'sonar',
    'sonar-pro',
    'sonar-reasoning',
    'sonar-reasoning-pro',
    'gpt-4.5-preview',
    'gemini-2.0-flash-thinking-exp-01-21',
    'deepseek-ai/DeepSeek-R1',
    'o1',
    'o3-mini',
    'o3-mini-high',
    'claude-3-7-sonnet-thinking',
    'claude-sonnet-4-5',
    'claude-4-5-sonnet-thinking',
    'claude-opus-4-5',
    'claude-4-5-opus-thinking',
    'claude-sonnet-4-6',
    'claude-4-6-sonnet-thinking',
    'claude-opus-4-6',
    'claude-4-6-opus-thinking',
    'claude-opus-4-7',
    'claude-4-7-opus-thinking',
    'gpt-5.5',
    'grok-4.20-0309-reasoning',
    'deepseek/deepseek-v4-pro',
    'z-ai/glm-5.1'
  ];
  return premiumModels.includes(modelValue);
};

export const getModelDisplayName = (modelValue: string): string => {
  // Keep provider SKUs private in every user-facing surface. Internal model
  // ids remain unchanged for routing, billing, and analytics.
  if (modelValue.toLowerCase().includes('playbook')) return 'Playbook';
  if (modelValue.toLowerCase().includes('deepseek')) return 'DeepSeek';
  const model = getModelInfo(modelValue);
  return model?.label || modelValue;
};
